/**
 * Right-side cluster of action buttons in the header:
 * theme toggle, fullscreen toggle, offline-sync badge, settings,
 * ops_manager schedule-flight CTA, and the desktop user avatar.
 *
 * Schedule-flight modal mount lives in the parent so the coach mark + modal
 * can share the same isOpsManager guard without prop drilling.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Maximize2, Minimize2, Moon, Plus, Settings, Sun } from "lucide-react";
import OfflineSyncBadge from "@/components/OfflineSyncBadge";

export default function HeaderActions({ user, isOpsManager, onOpenSchedule }) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <>
      {/* Theme Toggle */}
      <button
        data-testid="theme-toggle"
        onClick={toggleTheme}
        className="p-1.5 sm:p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors duration-200"
        title={theme === "dark" ? t("common.switchToLight") : t("common.switchToDark")}
        aria-label={theme === "dark" ? t("common.switchToLight") : t("common.switchToDark")}
      >
        {theme === "dark" ? (
          <Sun className="w-4 h-4 sm:w-5 sm:h-5" />
        ) : (
          <Moon className="w-4 h-4 sm:w-5 sm:h-5" />
        )}
      </button>

      {/* Fullscreen Toggle — desktop only */}
      <button
        onClick={() => {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {});
          } else {
            document.exitFullscreen().catch(() => {});
          }
        }}
        className="hidden sm:flex p-1.5 sm:p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors duration-200"
        title={document.fullscreenElement ? t("common.exitFullscreen") : t("common.fullscreen")}
      >
        {document.fullscreenElement ? (
          <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" />
        ) : (
          <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />
        )}
      </button>

      {/* NotificationSnoozeButton removido: iOS / Android ya tienen
          "no molestar" a nivel sistema, redundante. */}

      {/* Pending offline writes — only renders when count > 0. */}
      <OfflineSyncBadge />

      {/* Settings */}
      <button
        data-testid="settings-btn"
        onClick={() => navigate("/settings")}
        className="p-2 sm:p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/50 active:bg-slate-800/50 active:scale-95 transition-all duration-200"
        title={t("nav.settings")}
        aria-label={t("nav.settings")}
      >
        <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>

      {/* Programar vuelo — sólo mobile, sólo ops_manager. CTA principal
          del header en mobile. En desktop el botón vive inline en el OCC
          (DashboardPage), junto al título. */}
      {isOpsManager && (
        <button
          data-testid="topheader-schedule-flight"
          onClick={onOpenSchedule}
          className="sm:hidden flex items-center gap-1.5 px-2 py-1.5 rounded-xl bg-altiv-navy text-white hover:opacity-90 active:scale-95 transition-all duration-150"
          title={t("dashboard.scheduleFlight")}
          aria-label={t("dashboard.scheduleFlight")}
        >
          <Plus className="w-4 h-4" />
        </button>
      )}

      {/* User initial (avatar) — desktop only. Se quitó de mobile porque
          se desbordaba con la orilla derecha y el menú de usuario ya vive
          en /settings. */}
      <div className="hidden sm:flex items-center gap-2">
        <div className="w-8 h-8 bg-altiv-navy rounded-lg flex items-center justify-center text-white text-sm font-bold">
          {user?.full_name?.charAt(0) || "U"}
        </div>
      </div>
    </>
  );
}
